import { useCallback, useEffect, useRef, useState } from "react";
import {
  ANSWER_REVEAL_SOUND_DURATION_MS,
  createQuestionTimerAudio,
  GAME_OVER_SOUND_DURATION_MS,
  gameAudioFlags,
  ROUND_INTRO_SOUND_DURATION_MS,
  SOUND_CLIP_TAIL_MS,
  SOUND_PREFERENCE_KEY,
} from "./questionTimerAudio";

const createEngine = (path) => {
  try {
    return createQuestionTimerAudio(new Audio(`${process.env.PUBLIC_URL || ""}${path}`));
  } catch {
    return null; // Unsupported audio must never interrupt the display.
  }
};

// Plays a one-shot clip once from its stage's start, until the clip ends, the
// clip's optional endTime passes, or it stops being active, whichever is first.
// A key is spent once its clip stops being active: a stage that leaves and comes
// back for the same key (the final standings after the between-games ad) is
// silent. Only the latest spent key is kept, so a display that runs for days
// holds one key, not one per question.
function useStageClip(enginesRef, name, clip, clipDurationMs, soundEnabled, enabled) {
  const clipActive = Boolean(clip?.active);
  const clipStart = clip?.startTime;
  const clipEnd = clip?.endTime;
  const clipKey = clip?.key;
  const activeKeyRef = useRef(null);
  const spentKeyRef = useRef(null);
  useEffect(() => {
    const engine = enginesRef.current[name];
    engine?.setEnabled(soundEnabled);
    const key = `${name}:${clipKey}`;
    let valid = clipActive && Number.isFinite(clipStart);
    if (!valid && activeKeyRef.current) {
      spentKeyRef.current = activeKeyRef.current;
      activeKeyRef.current = null;
    }
    if (valid && activeKeyRef.current !== key) {
      if (spentKeyRef.current === key) {
        valid = false;
      } else {
        if (activeKeyRef.current) spentKeyRef.current = activeKeyRef.current;
        activeKeyRef.current = key;
      }
    }
    engine?.setWindow(valid ? {
      startTime: clipStart,
      endTime: Math.min(
        clipStart + clipDurationMs + SOUND_CLIP_TAIL_MS,
        Number.isFinite(clipEnd) ? clipEnd : Infinity,
      ),
      key,
    } : null);
  }, [enginesRef, name, clipActive, clipStart, clipEnd, clipKey, clipDurationMs, soundEnabled, enabled]);
}

// A clip the admin has silenced is inactive, so it is spent rather than resumed.
const allowedClip = (clip, allowed) => (clip ? { ...clip, active: Boolean(clip.active) && allowed } : clip);

// `allowed` is the admin's live game-sound setting from the server. The build
// flag decides whether audio exists at all; `allowed` and the display's own
// Sound toggle decide whether it plays. `allowed` closes the playback windows
// rather than muting the engines, so a gesture still primes them while the
// admin has sound off, and sound works when it is turned back on.
export default function useQuestionTimerAudio({
  active, countdown, questionKey, roundIntro, answerReveal, gameOver, allowed = true,
}) {
  const enabled = gameAudioFlags.enabled;
  const enginesRef = useRef({});
  const [soundEnabled, setSoundEnabled] = useState(() => {
    if (!enabled) return true;
    try { return localStorage.getItem(SOUND_PREFERENCE_KEY) !== "false"; }
    catch { return true; }
  });
  useEffect(() => {
    if (!enabled) return undefined;
    const engines = {
      timer: createEngine("/audio/question-timer.wav"),
      roundIntro: createEngine("/audio/round_intro.mp3"),
      answerReveal: createEngine("/audio/answer_reveal.mp3"),
      gameOver: createEngine("/audio/game-over.mp3"),
    };
    enginesRef.current = engines;
    return () => {
      Object.values(engines).forEach((engine) => engine?.dispose());
      enginesRef.current = {};
    };
  }, [enabled]);

  const { visible, startTime, endTime } = countdown;
  useEffect(() => {
    const engine = enginesRef.current.timer;
    engine?.setEnabled(soundEnabled);
    const valid = allowed && active && visible && Number.isFinite(startTime) &&
      Number.isFinite(endTime) && endTime > startTime;
    engine?.setWindow(valid ? {
      startTime, endTime, key: `${questionKey}:${startTime}:${endTime}`,
    } : null);
  }, [allowed, active, visible, startTime, endTime, questionKey, soundEnabled, enabled]);

  // Once per round, as the round's first question intro appears.
  useStageClip(enginesRef, "roundIntro", allowedClip(roundIntro, allowed), ROUND_INTRO_SOUND_DURATION_MS, soundEnabled, enabled);
  // Once per question, as the correct answer is shown.
  useStageClip(enginesRef, "answerReveal", allowedClip(answerReveal, allowed), ANSWER_REVEAL_SOUND_DURATION_MS, soundEnabled, enabled);
  // Once per game, as the final standings appear. The boards rotate within one
  // stage, so the rotation never restarts it.
  useStageClip(enginesRef, "gameOver", allowedClip(gameOver, allowed), GAME_OVER_SOUND_DURATION_MS, soundEnabled, enabled);

  const unlockAudio = useCallback(() => {
    Object.values(enginesRef.current).forEach((engine) => engine?.unlock());
  }, []);
  const toggleSound = useCallback(() => {
    const next = !soundEnabled;
    const engines = Object.values(enginesRef.current);
    engines.forEach((engine) => engine?.setEnabled(next)); // Muting stops playback immediately.
    setSoundEnabled(next);
    try { localStorage.setItem(SOUND_PREFERENCE_KEY, String(next)); } catch { /* Optional. */ }
    if (next) engines.forEach((engine) => engine?.unlock());
  }, [soundEnabled]);
  // The Sound toggle is hidden while the admin has game sounds off.
  return { audioEnabled: enabled && allowed, soundEnabled, toggleSound, unlockAudio };
}
